import { useState, useEffect, useRef } from "react";
import {
  createInitialState,
  ensureWorldAhead,
  updateState,
  render,
} from "@/lib/gameEngine";
import {
  playSound,
  unlockAudio,
  setMuted,
  isMuted,
  stopMusic,
} from "@/lib/sfx";

// ---------- Pure builders (kept out of the hook body to reduce complexity) ----------

function applyKeyDown(event, gameState, actions) {
  const st = gameState;
  if (!st) return;
  unlockAudio();
  const { code } = event;
  if (code === "ArrowLeft" || code === "KeyA") st.keys.left = true;
  else if (code === "ArrowRight" || code === "KeyD") st.keys.right = true;
  else if (code === "Space" || code === "ArrowUp" || code === "KeyW") {
    st.keys.jump = true;
    event.preventDefault();
  } else if (code === "Escape" || code === "KeyP") {
    actions.togglePaused();
  } else if (code === "KeyM") {
    actions.toggleMuted();
  }
}

function applyKeyUp(event, gameState) {
  const st = gameState;
  if (!st) return;
  const { code } = event;
  if (code === "ArrowLeft" || code === "KeyA") st.keys.left = false;
  else if (code === "ArrowRight" || code === "KeyD") st.keys.right = false;
  else if (code === "Space" || code === "ArrowUp" || code === "KeyW") {
    st.keys.jump = false;
  }
}

/** Advance one frame; returns true when the run is in the game-over state. */
function stepFrame(ctx, stateRef, pausedRef) {
  const st = stateRef.current;
  if (!st || !ctx) return false;
  if (!pausedRef.current && st.state === "playing") {
    updateState(st);
    if (st.pendingSounds.length) {
      for (const name of st.pendingSounds) playSound(name);
      st.pendingSounds.length = 0;
    }
  }
  render(ctx, st);
  return st.state === "gameover";
}

// ---------- Hook ----------

/**
 * Drives the game: owns the canvas context, runs a continuous
 * requestAnimationFrame loop (never terminated so restart works cleanly),
 * binds keyboard handlers, and drains queued SFX. The HUD reads the latest
 * values from `stateRef` each frame via a forced re-render.
 */
export function useGameLoop({ mode, onGameOver }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const onGameOverRef = useRef(onGameOver);
  // Ensures we announce game-over exactly once per run (loop keeps running).
  const gameOverAnnouncedRef = useRef(false);

  const [paused, setPausedState] = useState(false);
  const [showOver, setShowOver] = useState(false);
  const [muted, setMutedState] = useState(false);
  const [, force] = useState(0);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);
  useEffect(() => {
    onGameOverRef.current = onGameOver;
  }, [onGameOver]);

  const toggleMute = () => {
    unlockAudio();
    const next = !isMuted();
    setMuted(next);
    setMutedState(next);
  };

  const restart = () => {
    const s = createInitialState({ mode });
    ensureWorldAhead(s);
    stateRef.current = s;
    gameOverAnnouncedRef.current = false;
    setShowOver(false);
    setPausedState(false);
  };

  const setTouchKey = (key, val) => {
    const st = stateRef.current;
    if (!st) return;
    unlockAudio();
    st.keys[key] = val;
  };

  useEffect(() => {
    const s = createInitialState({ mode });
    ensureWorldAhead(s);
    stateRef.current = s;
    gameOverAnnouncedRef.current = false;
    setShowOver(false);

    const actions = {
      togglePaused: () => setPausedState((p) => !p),
      toggleMuted: () => {
        const next = !isMuted();
        setMuted(next);
        setMutedState(next);
      },
    };
    const handleKeyDown = (e) => applyKeyDown(e, stateRef.current, actions);
    const handleKeyUp = (e) => applyKeyUp(e, stateRef.current);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    const ctx = canvasRef.current ? canvasRef.current.getContext("2d") : null;

    // Continuous loop — never returns early. On game-over we announce once
    // via the ref guard, then keep rendering the frozen final frame so that
    // `restart()` only needs to swap `stateRef.current`.
    const loop = () => {
      const ended = stepFrame(ctx, stateRef, pausedRef);
      force((x) => (x + 1) % 1000);
      if (ended && !gameOverAnnouncedRef.current) {
        gameOverAnnouncedRef.current = true;
        setShowOver(true);
        stopMusic();
        const cb = onGameOverRef.current;
        if (typeof cb === "function") cb(stateRef.current);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [mode]);

  return {
    canvasRef,
    stateRef,
    paused,
    setPaused: setPausedState,
    showOver,
    muted,
    toggleMute,
    restart,
    setTouchKey,
  };
}
